import { useDocumentListingLazyQuery } from '@account-root/procurement-react/src/generated/graphql'
import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import { UploadPreview } from '@ifca-root/react-component/src/components/Avatar/UploadPreview'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import { Footer } from '@ifca-root/react-component/src/components/Footer/Footer'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { FileUploadInput } from '@ifca-root/react-component/src/components/Input/FileUploadInput'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import useUploadAttachment from '@ifca-root/react-component/src/utils/hooks/useUploadAttachment'
import { MenuItem, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { AccCodeDropdownFullWidth } from 'components/Dropdown/AccCodeDropdown'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaDisposalMutation,
  useGetAssetRegistrationQuery,
  useGetCurrentAssetDisposalInfoLazyQuery,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useUpdateFaDisposalMutation,
  useGetAssetDepartmentsForDisposalLazyQuery,
  useGetAssetLocationsForDisposalLazyQuery,
  useGetDisposalBalanceQuantityLazyQuery,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import React, { useContext, useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router'
import * as yup from 'yup'

interface RevaluationProps {
  AssetNo: any
  AssetName: any
  Location: any
  Department?: string
  CostCentreID?: string
  fa_location_id?: string
  BalQtyAfterDisposal: string
  BalQtyBeforeDisposal: string
  DeptLocBalBefore: string
  DeptLocBalAfter: string
  unitPrice: string
  OriginalValue: string
  TotalDepreciate: string
  DisposalDate: Date
  DisposalQty: any
  DisposalMethod: any
  DisposalExpenses: string
  BookValue: string
  DisposalValue: string
  DisposalGainLoss: string
  Reason: any
  Attachment: any
}

const normalizeNumber = (value?: number | string | null) => {
  if (value === undefined || value === null || value === '') {
    return 0
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  const parsed = Number(value.replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const formatDisplay = (value?: number | string | null) =>
  normalizeNumber(value).toFixed(2)

const formatForDisplay = (value?: number | string | null) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(normalizeNumber(value))

export const WriteOffDisposalForm = (props: any) => {
  const isGLSubscribe = JSON.parse(localStorage.getItem('isGLSubscribe'))
  let history = useHistory()
  let location = useLocation()
  const { CompanyID }: any = useParams()
  const { formMode, type } = props
  const mode = formMode
  const editData: any = location?.state
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const [voiceRemark, setVoiceRemark] = useState('')
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(null)
  const [currentAssetInfo, setCurrentAssetInfo] = useState<any>(null)

  // State for department/location specific data
  const [assetDepartments, setAssetDepartments] = useState<any[]>([])
  const [assetLocations, setAssetLocations] = useState<any[]>([])
  const [deptLocBalance, setDeptLocBalance] = useState<any>(null)
  const bankAccountScheme = yup.object().shape({
    AssetNo: yup.string().required('Asset is required'),
    DisposalDate: yup.date().required('Disposal Date is required'),
    DisposalMethod: yup.string().required('Disposal Method is required'),
    DisposalQty: yup
      .number()
      .required('Disposal Qty is required')
      .min(1, 'Must be at least 1'),
    Reason: yup.string().required('Reason is required'),
  })

  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(
    SnackBarContext as any
  )

  const {
    handleSubmit,
    control,
    register,
    watch,
    setError,
    clearErrors,
    errors,
    setValue,
    formState: { isDirty },
  } = useForm<RevaluationProps>({
    mode: 'onSubmit',
    resolver: yupResolver(bankAccountScheme),
    defaultValues: {
      AssetNo: '',
      AssetName: '',
      Location: '',
      Department: '',
      CostCentreID: '',
      fa_location_id: '',
      BalQtyAfterDisposal: '0',
      BalQtyBeforeDisposal: '0',
      DeptLocBalBefore: '0',
      DeptLocBalAfter: '0',
      unitPrice: '0.00',
      OriginalValue: '0.00',
      TotalDepreciate: '0.00',
      BookValue: '0.00',
      DisposalDate: new Date(),
      DisposalQty: 0,
      DisposalMethod: '',
      DisposalExpenses: '0.00',
      DisposalValue: '0.00',
      DisposalGainLoss: '0.00',
      Reason: '',
      Attachment: null,
    },
  })

  const readOnlyUpdateOptions = {
    shouldDirty: false,
    shouldValidate: false,
    shouldTouch: true,
  }

  const resetAssetDerivedFields = () => {
    setValue('AssetName', '', readOnlyUpdateOptions)
    setValue('OriginalValue', formatDisplay(0), readOnlyUpdateOptions)
    setValue('unitPrice', formatDisplay(0), readOnlyUpdateOptions)
    setValue('TotalDepreciate', formatDisplay(0), readOnlyUpdateOptions)
    setValue('BookValue', formatDisplay(0), readOnlyUpdateOptions)
    setValue('BalQtyBeforeDisposal', '0', readOnlyUpdateOptions)
    setValue('BalQtyAfterDisposal', '0', readOnlyUpdateOptions)
    setValue('DeptLocBalBefore', '0', readOnlyUpdateOptions)
    setValue('DeptLocBalAfter', '0', readOnlyUpdateOptions)
    setValue('CostCentreID', '', readOnlyUpdateOptions)
    setValue('fa_location_id', '', readOnlyUpdateOptions)
    setValue('DisposalGainLoss', formatForDisplay(0), readOnlyUpdateOptions)
    setAssetDepartments([])
    setAssetLocations([])
    setDeptLocBalance(null)
  }

  const [
    getAssetDepartments,
    { loading: assetDepartmentsLoading },
  ] = useGetAssetDepartmentsForDisposalLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      if (data?.getAssetDepartmentsForDisposal) {
        setAssetDepartments(data.getAssetDepartmentsForDisposal)
      }
    },
  })

  const [
    getAssetLocations,
    { loading: assetLocationsLoading },
  ] = useGetAssetLocationsForDisposalLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      if (data?.getAssetLocationsForDisposal) {
        setAssetLocations(data.getAssetLocationsForDisposal)
      }
    },
  })

  const [
    getDisposalBalanceQuantity,
    { loading: balanceLoading },
  ] = useGetDisposalBalanceQuantityLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      if (data?.getDisposalBalanceQuantity) {
        const balance = data.getDisposalBalanceQuantity
        setDeptLocBalance(balance)
        setValue(
          'DeptLocBalBefore',
          balance.balance_before_disposal?.toString() || '0',
          readOnlyUpdateOptions
        )
        setValue(
          'DeptLocBalAfter',
          balance.balance_after_disposal?.toString() || '0',
          readOnlyUpdateOptions
        )
      }
    },
  })

  const {
    loading: getAssetRegistrationLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
  })

  const {
    files,
    setFiles,
    setPreviewFiles,
    handleUploadChange,
    previewFiles,
    remove: removeFile,
    handleEditUpload,
  } = useUploadAttachment()

  const [
    fetchDocuments,
    { loading: documentLoading, error: DocError, data: DocData },
  ] = useDocumentListingLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ DocumentListing }) => {
      if (!editData?.files) {
        handleEditUpload(
          DocumentListing?.filter(doc => doc?.description !== 'document_pdf')
        )
        previewFiles?.push(
          ...DocumentListing?.filter(
            doc => doc?.description !== 'document_pdf'
          )?.map(x => x?.fileURL)
        )
      }
    },
  })

  const [
    getCurrentAssetDisposalInfo,
    { loading: assetInfoLoading },
  ] = useGetCurrentAssetDisposalInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      if (data?.getCurrentAssetDisposalInfo) {
        const info = data.getCurrentAssetDisposalInfo
        setCurrentAssetInfo(info)

        setValue('OriginalValue', formatForDisplay(info.originalValue), {
          shouldDirty: true,
        })
        setValue('unitPrice', formatForDisplay(info.unitPrice), {
          shouldDirty: true,
        })
        setValue(
          'TotalDepreciate',
          formatForDisplay(info.totalAccumulatedDepreciation),
          { shouldDirty: true }
        )
        setValue('BookValue', formatForDisplay(info.currentBookValue), {
          shouldDirty: true,
        })
        setValue('BalQtyBeforeDisposal', info.currentQty.toString(), {
          shouldDirty: true,
        })
        setValue('BalQtyAfterDisposal', info.currentQty.toString(), {
          shouldDirty: true,
        })
        setValue('DisposalGainLoss', formatForDisplay(0), {
          shouldDirty: true,
        })
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  const [
    CreateFADisposal,
    { loading: CreateFADisposalLoading, data: CreateFADisposalData },
  ] = useCreateFaDisposalMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push({
        pathname: `/fixed-assets/${CompanyID}/write-off-disposal`,
      })
    },
  })

  const [
    UpdateFADisposal,
    { loading: UpdateFADisposalLoading, data: UpdateFADisposalData },
  ] = useUpdateFaDisposalMutation({
    onError: ({ message }) => {
      let error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push({
        pathname: `/fixed-assets/${CompanyID}/write-off-disposal`,
      })
    },
  })

  const disposalMethod = [
    { DisposalMethod: 'Disposal' },
    { DisposalMethod: 'Write Off' },
  ]

  const watchDisposalQty = watch('DisposalQty', '0')
  const watchDisposalValue = watch('DisposalValue', '0')
  const watchDisposalExpenses = watch('DisposalExpenses', '0')
  const watchDisposalMethod = watch('DisposalMethod', '')
  const watchCostCentreID = watch('CostCentreID', '')
  const watchLocationID = watch('fa_location_id', '')

  const handleAssetChange = (assetId: string) => {
    if (!assetId) {
      resetAssetDerivedFields()
      setSelectedAsset(null)
      setCurrentAssetInfo(null)
      return
    }

    const normalizedAssetId = String(assetId)
    const asset = getAssetRegistration?.find(
      el => String(el.fa_asset_id) === normalizedAssetId
    )

    if (!asset) {
      resetAssetDerivedFields()
      setSelectedAsset(null)
      setCurrentAssetInfo(null)
      return
    }
    setSelectedAsset(asset)
    clearErrors('AssetNo')

    setValue('AssetName', asset.name || '', { shouldDirty: true })
    setValue('CostCentreID', '', { shouldDirty: true })
    setValue('fa_location_id', '', { shouldDirty: true })
    setAssetLocations([])
    setDeptLocBalance(null)
    setValue('DeptLocBalBefore', '0', readOnlyUpdateOptions)
    setValue('DeptLocBalAfter', '0', readOnlyUpdateOptions)
    getAssetDepartments({
      variables: {
        fa_asset_id: normalizedAssetId,
        disposal_id: editData?.disposal_id || null,
      },
    })
    getCurrentAssetDisposalInfo({
      variables: {
        fa_asset_id: normalizedAssetId,
        disposal_id: editData?.disposal_id || null,
      },
    })
  }

  const handleDepartmentChange = (costCentreId: string) => {
    setValue('CostCentreID', costCentreId, { shouldDirty: true })
    setValue('fa_location_id', '', { shouldDirty: true })
    setAssetLocations([])
    setDeptLocBalance(null)
    setValue('DeptLocBalBefore', '0', readOnlyUpdateOptions)
    setValue('DeptLocBalAfter', '0', readOnlyUpdateOptions)

    if (costCentreId && selectedAsset?.fa_asset_id) {
      getAssetLocations({
        variables: {
          fa_asset_id: selectedAsset.fa_asset_id,
          cost_centre_id: costCentreId,
          disposal_id: editData?.disposal_id || null,
        },
      })
    }
  }

  const handleLocationChange = (locationId: string) => {
    setValue('fa_location_id', locationId, { shouldDirty: true })

    const costCentreId = watch('CostCentreID')
    const disposalQty = normalizeNumber(watchDisposalQty)

    if (locationId && costCentreId && selectedAsset?.fa_asset_id) {
      getDisposalBalanceQuantity({
        variables: {
          fa_asset_id: selectedAsset.fa_asset_id,
          cost_centre_id: costCentreId,
          location_id: locationId,
          disposal_qty: disposalQty || 0,
          disposal_id: editData?.disposal_id || null,
        },
      })
    } else {
      setDeptLocBalance(null)
      setValue('DeptLocBalBefore', '0', readOnlyUpdateOptions)
      setValue('DeptLocBalAfter', '0', readOnlyUpdateOptions)
    }
  }

  useEffect(() => {
    if (selectedAsset && currentAssetInfo) {
      const disposalQty = normalizeNumber(watchDisposalQty)
      const disposalValue = normalizeNumber(watchDisposalValue)

      const currentQty = normalizeNumber(currentAssetInfo.currentQty)
      const currentBookValue = normalizeNumber(
        currentAssetInfo.currentBookValue
      )
      const totalAccumulatedDepreciation = normalizeNumber(
        currentAssetInfo.totalAccumulatedDepreciation
      )

      const balQtyAfter = Math.max(0, currentQty - disposalQty)
      setValue(
        'BalQtyAfterDisposal',
        balQtyAfter.toString(),
        readOnlyUpdateOptions
      )

      const bookValuePerUnit =
        currentQty > 0 ? currentBookValue / currentQty : 0
      const accDepreciationPerUnit =
        currentQty > 0 ? totalAccumulatedDepreciation / currentQty : 0

      const disposedBookValue = bookValuePerUnit * disposalQty

      if (disposalQty > 0) {
        setValue(
          'BookValue',
          formatForDisplay(disposedBookValue),
          readOnlyUpdateOptions
        )
      }

      const gainLoss = disposalValue - disposedBookValue

      setValue(
        'DisposalGainLoss',
        formatForDisplay(gainLoss),
        readOnlyUpdateOptions
      )
    }
  }, [
    watchDisposalQty,
    watchDisposalValue,
    selectedAsset,
    currentAssetInfo,
    setValue,
  ])

  useEffect(() => {
    const costCentreId = watchCostCentreID
    const locationId = watchLocationID
    const disposalQty = normalizeNumber(watchDisposalQty)

    if (selectedAsset?.fa_asset_id && costCentreId && locationId) {
      getDisposalBalanceQuantity({
        variables: {
          fa_asset_id: selectedAsset.fa_asset_id,
          cost_centre_id: costCentreId,
          location_id: locationId,
          disposal_qty: disposalQty || 0,
          disposal_id: editData?.disposal_id || null,
        },
      })
    }
  }, [watchDisposalQty, watchCostCentreID, watchLocationID, selectedAsset])

  useEffect(() => {
    const methodsThatRequireZeroValue = [
      'Write Off',
      'Donation',
      'Lost',
      'Destroyed',
    ]

    if (
      watchDisposalMethod &&
      methodsThatRequireZeroValue.includes(watchDisposalMethod)
    ) {
      setValue('DisposalValue', '0.00', { shouldDirty: true })
    }
  }, [watchDisposalMethod, setValue])

  /* -------------------------------------------- */
  /*               PERIOD VALIDATION              */
  /* -------------------------------------------- */
  const watchDisposalDate =
    watch('DisposalDate') || editData?.DisposalDate || new Date()
  const disposalDateStr = (() => {
    const d = new Date(watchDisposalDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? disposalDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        disposalDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const disposalDate = new Date(watchDisposalDate)
  const isDocDateInvalid =
    disposalDate.getTime() < minValidDate.getTime() ||
    disposalDate.getTime() > maxValidDate.getTime()

  const onSubmit = async (
    data: RevaluationProps,
    status: ApprovalStatus.Active | ApprovalStatus.Submit
  ) => {
    if (status === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMessage(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrDialog(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMessage(
          'Cannot submit. The disposal date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrDialog(true)
        return
      }
    }

    if (!selectedAsset) {
      setError('AssetNo', { message: 'Please select an asset' })
      return
    }

    if (!currentAssetInfo) {
      setErrMessage('Please wait for asset information to load')
      setErrDialog(true)
      return
    }

    const disposalQty = normalizeNumber(data.DisposalQty)
    const currentQty = currentAssetInfo.currentQty || 0

    if (disposalQty > currentQty) {
      setErrMessage(
        `Disposal quantity (${disposalQty}) cannot exceed available quantity (${currentQty}).`
      )
      setErrDialog(true)
      return
    }

    if (disposalQty <= 0) {
      setError('DisposalQty', {
        message: 'Disposal quantity must be greater than 0',
      })
      return
    }

    const bookValuePerUnit = currentAssetInfo.bookValuePerUnit || 0
    const disposedBookValue = bookValuePerUnit * disposalQty

    const totalAccumulatedDepreciation =
      currentAssetInfo.totalAccumulatedDepreciation || 0
    const accDepreciationPerUnit =
      currentQty > 0 ? totalAccumulatedDepreciation / currentQty : 0
    const disposedAccDepreciation = accDepreciationPerUnit * disposalQty

    if (!data.CostCentreID) {
      setErrMessage('Please select a department.')
      setErrDialog(true)
      return
    }

    if (!data.fa_location_id) {
      setErrMessage('Please select a location.')
      setErrDialog(true)
      return
    }

    if (deptLocBalance && !deptLocBalance.is_valid) {
      setErrMessage(
        deptLocBalance.error_message ||
          'Invalid disposal quantity for selected department/location.'
      )
      setErrDialog(true)
      return
    }

    const input = {
      fa_asset_id: selectedAsset.fa_asset_id,
      company_id: CompanyID,
      cost_centre_id: data.CostCentreID,
      location_id: data.fa_location_id,
      disposal_date: data.DisposalDate,
      disposal_method: data.DisposalMethod,
      disposal_qty: disposalQty,
      disposal_value: normalizeNumber(data.DisposalValue),
      disposal_expenses: normalizeNumber(data.DisposalExpenses),
      book_value: disposedBookValue,
      accumulated_depreciation: disposedAccDepreciation,
      disposal_gain_loss: normalizeNumber(data.DisposalGainLoss),
      reason: data.Reason,
      ApprovalStatus: status,
      File: files?.length > 0 ? files : undefined,
      ...((mode === 'edit' || mode === 'resubmit') &&
        editData?.disposal_id && {
          disposal_id: editData.disposal_id,
        }),
    }

    if (mode === 'edit') {
      await UpdateFADisposal({
        variables: { input },
      })
    } else if (mode === 'resubmit') {
      await UpdateFADisposal({
        variables: { input },
      })
    } else {
      await CreateFADisposal({
        variables: { input },
      })
    }
  }

  useEffect(() => {
    if (
      (mode === 'edit' || mode === 'resubmit') &&
      editData &&
      getAssetRegistration?.length > 0
    ) {
      const asset = getAssetRegistration?.find(
        el => String(el.fa_asset_id) === String(editData.fa_asset_id)
      )

      if (asset) {
        setValue('AssetNo', String(asset.fa_asset_id), { shouldDirty: false })
        setSelectedAsset(asset)
        getCurrentAssetDisposalInfo({
          variables: {
            fa_asset_id: String(asset.fa_asset_id),
            disposal_id: editData?.disposal_id || null,
          },
        })
        getAssetDepartments({
          variables: {
            fa_asset_id: String(asset.fa_asset_id),
            disposal_id: editData?.disposal_id || null,
          },
        })

        setValue('AssetName', asset.name || '', { shouldDirty: false })

        if (editData?.disposal_id) {
          fetchDocuments({
            variables: {
              refID: editData.disposal_id,
            },
          })
        }

        setTimeout(() => {
          if (editData.disposal_date) {
            setValue('DisposalDate', new Date(editData.disposal_date), {
              shouldDirty: false,
            })
          }

          if (editData.disposal_method) {
            setValue('DisposalMethod', editData.disposal_method, {
              shouldDirty: false,
            })
          }

          if (
            editData.disposal_qty !== undefined &&
            editData.disposal_qty !== null
          ) {
            setValue('DisposalQty', editData.disposal_qty.toString(), {
              shouldDirty: false,
            })
          }

          if (
            editData.disposal_value !== undefined &&
            editData.disposal_value !== null
          ) {
            setValue('DisposalValue', formatDisplay(editData.disposal_value), {
              shouldDirty: false,
            })
          }

          if (
            editData.disposal_expenses !== undefined &&
            editData.disposal_expenses !== null
          ) {
            setValue(
              'DisposalExpenses',
              formatDisplay(editData.disposal_expenses),
              { shouldDirty: false }
            )
          }

          if (editData.reason) {
            setValue('Reason', editData.reason, { shouldDirty: false })
            setVoiceRemark(editData.reason)
          }

          if (editData.cost_centre_id) {
            setValue('CostCentreID', editData.cost_centre_id, {
              shouldDirty: false,
            })

            getAssetLocations({
              variables: {
                fa_asset_id: String(asset.fa_asset_id),
                cost_centre_id: editData.cost_centre_id,
                disposal_id: editData?.disposal_id || null,
              },
            })
          }

          setTimeout(() => {
            if (editData.location_id) {
              setValue('fa_location_id', editData.location_id, {
                shouldDirty: false,
              })

              if (editData.cost_centre_id) {
                getDisposalBalanceQuantity({
                  variables: {
                    fa_asset_id: String(asset.fa_asset_id),
                    cost_centre_id: editData.cost_centre_id,
                    location_id: editData.location_id,
                    disposal_qty: editData.disposal_qty || 0,
                    disposal_id: editData?.disposal_id || null,
                  },
                })
              }
            }
          }, 300)
        }, 100)
      }
    }
  }, [mode, editData, getAssetRegistration])

  return (
    <>
      {(getAssetRegistrationLoading ||
        CreateFADisposalLoading ||
        UpdateFADisposalLoading ||
        assetInfoLoading ||
        assetDepartmentsLoading ||
        assetLocationsLoading ||
        balanceLoading ||
        openPeriodCheckDateLoading ||
        docDateValidationLoading) && <Loading />}
      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(`/fixed-assets/${CompanyID}/write-off-disposal`)
        }}
        smTitle={'Fixed Assets'}
        title={user?.accountName}
        routeSegments={[
          { name: 'Company Setting' },
          { name: 'Company' },
          { name: 'Company' },
          { name: 'Write-Off/Disposal', current: true },
        ]}
        rightRouteSegments={[
          { name: mode === 'add' ? 'New' : 'Edit', current: true },
        ]}
      />

      <ContentWrapper footer>
        <CardContents
          section={{
            header: {
              title: 'Assets Information',
            },
          }}
        >
          <Controller
            render={({ value, onChange }) => {
              const selectedAssetOption = getAssetRegistration
                ?.sort((a, b) => a?.DocNo?.localeCompare(b?.DocNo))
                ?.find(asset => asset?.fa_asset_id === value)

              return (
                <Autocomplete
                  options={
                    getAssetRegistration?.sort((a, b) =>
                      a?.DocNo?.localeCompare(b?.DocNo)
                    ) || []
                  }
                  value={selectedAssetOption || null}
                  getOptionLabel={option =>
                    option ? `${option?.DocNo} - ${option?.name}` : ''
                  }
                  fullWidth
                  onChange={(event, newValue: any) => {
                    const newAssetId = newValue?.fa_asset_id || ''
                    onChange(newAssetId)
                    handleAssetChange(newAssetId)
                  }}
                  disabled={mode === 'approve-reject'}
                  PopperComponent={AccCodeDropdownFullWidth}
                  renderOption={option => (
                    <div>
                      <div>
                        <span className="xsTitle">{option?.DocNo}</span>
                        <span className="desc" style={{ marginLeft: '8px' }}>
                          {option?.name}
                        </span>
                      </div>
                    </div>
                  )}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Asset"
                      InputLabelProps={
                        selectedAssetOption ? { shrink: true } : undefined
                      }
                      style={{ width: '100%' }}
                      required
                      margin="normal"
                      helperText={errors?.AssetNo?.message}
                      error={errors?.AssetNo ? true : false}
                      disabled={mode === 'approve-reject'}
                    />
                  )}
                />
              )
            }}
            name="AssetNo"
            control={control}
          />

          <Controller
            render={({ value, onChange }) => {
              const selectedDept = assetDepartments?.find(
                dept => dept?.cost_centre_id === value
              )

              return (
                <Autocomplete
                  options={assetDepartments || []}
                  value={selectedDept || null}
                  getOptionLabel={option =>
                    option
                      ? `${option?.cost_centre_code ||
                          ''} | ${option?.cost_centre_name || ''}`
                      : ''
                  }
                  fullWidth
                  onChange={(event, newValue: any) => {
                    const newCostCentreId = newValue?.cost_centre_id || ''
                    onChange(newCostCentreId)
                    handleDepartmentChange(newCostCentreId)
                  }}
                  disabled={mode === 'approve-reject' || !selectedAsset}
                  loading={assetDepartmentsLoading}
                  noOptionsText={
                    selectedAsset
                      ? 'No departments with available quantity'
                      : 'Please select an asset first'
                  }
                  PopperComponent={AccCodeDropdownFullWidth}
                  renderOption={option => (
                    <div>
                      <div>
                        <span className="xsTitle">
                          {option?.cost_centre_code || ''}
                        </span>
                      </div>
                      <div className="desc">
                        {option?.cost_centre_name || ''}
                      </div>
                      <div className="desc" style={{ color: '#4caf50' }}>
                        Available: {option?.available_qty || 0} | Registered:{' '}
                        {option?.registered_qty || 0}
                      </div>
                    </div>
                  )}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Department"
                      InputLabelProps={
                        selectedDept ? { shrink: true } : undefined
                      }
                      style={{ width: '100%' }}
                      margin="normal"
                      required
                      disabled={mode === 'approve-reject' || !selectedAsset}
                      helperText={
                        !selectedAsset
                          ? 'Select an asset first'
                          : assetDepartments?.length === 0
                          ? 'No departments available for this asset'
                          : ''
                      }
                    />
                  )}
                />
              )
            }}
            name="CostCentreID"
            defaultValue=""
            className="left"
            control={control}
          />

          <Controller
            render={({ value, onChange }) => {
              const selectedLocation = assetLocations?.find(
                loc => loc?.location_id === value
              )
              const costCentreId = watch('CostCentreID')

              return (
                <Autocomplete
                  options={assetLocations || []}
                  value={selectedLocation || null}
                  getOptionLabel={option =>
                    option ? `${option?.location_name || ''} ` : ''
                  }
                  fullWidth
                  onChange={(event, newValue: any) => {
                    const newLocationId = newValue?.location_id || ''
                    onChange(newLocationId)
                    handleLocationChange(newLocationId)
                  }}
                  disabled={mode === 'approve-reject' || !costCentreId}
                  loading={assetLocationsLoading}
                  noOptionsText={
                    costCentreId
                      ? 'No locations with available quantity'
                      : 'Please select a department first'
                  }
                  PopperComponent={AccCodeDropdownFullWidth}
                  renderOption={option => (
                    <div>
                      <div>
                        <span className="xsTitle">
                          {option?.location_name || ''}
                        </span>
                        {option?.location_description && (
                          <span className="desc">
                            {' '}
                            - {option.location_description}
                          </span>
                        )}
                      </div>
                      <div className="desc" style={{ color: '#4caf50' }}>
                        Available: {option?.available_qty || 0} | Registered:{' '}
                        {option?.registered_qty || 0}
                      </div>
                    </div>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Location"
                      InputLabelProps={
                        selectedLocation ? { shrink: true } : undefined
                      }
                      margin="normal"
                      required
                      disabled={mode === 'approve-reject' || !costCentreId}
                      helperText={
                        !costCentreId
                          ? 'Select a department first'
                          : assetLocations?.length === 0
                          ? 'No locations available for this department'
                          : ''
                      }
                    />
                  )}
                />
              )
            }}
            className="right"
            name="fa_location_id"
            defaultValue=""
            control={control}
          />

          <Controller
            render={({ value, onChange }) => (
              <TextField
                value={value || ''}
                label="Asset Name"
                autoComplete="off"
                fullWidth
                helperText={errors?.AssetName?.message}
                error={errors?.AssetName ? true : false}
                InputProps={{ readOnly: true }}
                disabled
              />
            )}
            name="AssetName"
            control={control}
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="BalQtyBeforeDisposal"
            label="Current Total Asset Balance"
            control={control}
            margin="dense"
            decimalScale={2}
            className="left"
            fixedDecimalScale
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            disabled
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="unitPrice"
            label="Unit Price"
            control={control}
            margin="dense"
            className="right"
            decimalScale={2}
            fixedDecimalScale
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            disabled
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="DeptLocBalBefore"
            label="Bal. Qty Before Disposal (Location)"
            control={control}
            margin="dense"
            decimalScale={2}
            className="left"
            fixedDecimalScale
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            disabled
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="DeptLocBalAfter"
            label="Bal. Qty After Disposal (Location)"
            control={control}
            margin="dense"
            decimalScale={2}
            className="right"
            fixedDecimalScale
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            style={{
              color:
                deptLocBalance && !deptLocBalance.is_valid
                  ? '#f44336'
                  : undefined,
            }}
            disabled
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="OriginalValue"
            label="Current Original Value"
            autoComplete="off"
            control={control}
            margin="dense"
            className="left"
            decimalScale={2}
            fixedDecimalScale
            InputProps={{ disableUnderline: true, readOnly: true }}
            disabled
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="TotalDepreciate"
            label="Total Depreciate"
            control={control}
            margin="dense"
            className="right"
            decimalScale={2}
            fixedDecimalScale
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            disabled
          />
        </CardContents>

        <CardContents
          section={{
            header: {
              title: 'Write-Off/Disposal Details',
            },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              required
              as={KeyboardDatePicker}
              name="DisposalDate"
              label="Disposal Date"
              control={control}
              onChange={(date: Date | null) => date}
              format="dd MMM yyyy"
              value={mode === 'add' ? new Date() : watch('DisposalDate')}
              margin="dense"
              allowKeyboardControl
              inputRef={register({})}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              defaultValue={
                editData ? new Date(editData?.disposal_date) : new Date()
              }
              helperText={errors?.DisposalDate?.message}
              error={errors?.DisposalDate ? true : false}
              showTodayButton
              className="left"
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={
              <TextField select>
                {disposalMethod?.map((el, index) => (
                  <MenuItem
                    style={{ whiteSpace: 'normal' }}
                    value={el.DisposalMethod}
                    key={index}
                  >
                    <span className="text-noflow">{el.DisposalMethod}</span>
                  </MenuItem>
                ))}
              </TextField>
            }
            id="DisposalMethod"
            name="DisposalMethod"
            label="Disposal Method"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            helperText={errors?.DisposalMethod?.message}
            error={errors?.DisposalMethod ? true : false}
            ref={register}
            defaultValue={editData?.disposal_method || ''}
            className="right"
            disabled={mode === 'approve-reject'}
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="DisposalQty"
            label="Disposal Qty"
            autoComplete="off"
            margin="dense"
            decimalScale={2}
            className="left"
            ref={register}
            control={control}
            helperText={errors?.DisposalQty?.message}
            error={errors?.DisposalQty ? true : false}
            fixedDecimalScale
            defaultValue={0}
          ></Controller>

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="BalQtyAfterDisposal"
            label="Bal. Qty After Disposal (Department)"
            autoComplete="off"
            fullWidth
            control={control}
            margin="dense"
            decimalScale={2}
            className="right"
            helperText={errors?.BalQtyAfterDisposal?.message}
            error={errors?.BalQtyAfterDisposal ? true : false}
            defaultValue={0}
            InputProps={{ readOnly: true }}
            fixedDecimalScale
            disabled
          ></Controller>

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="DisposalExpenses"
            label="Disposal Expenses"
            autoComplete="off"
            margin="dense"
            className="left"
            ref={register}
            control={control}
            defaultValue={0}
            decimalScale={2}
            fixedDecimalScale
          ></Controller>

          <Controller
            render={({ value }) => (
              <TextField
                id="book-value-before-disposal"
                value={value || '0.00'}
                label="Book Value"
                autoComplete="off"
                margin="dense"
                className="right"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                disabled
              />
            )}
            name="BookValue"
            control={control}
            defaultValue="0.00"
          />

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            name="DisposalValue"
            label="Disposal Value"
            autoComplete="off"
            margin="dense"
            className="left"
            ref={register}
            control={control}
            defaultValue={0}
            decimalScale={2}
            fixedDecimalScale
            disabled={
              mode === 'approve-reject' ||
              ['Write Off', 'Donation', 'Lost', 'Destroyed'].includes(
                watchDisposalMethod
              )
            }
            InputProps={{
              readOnly: ['Write Off', 'Donation', 'Lost', 'Destroyed'].includes(
                watchDisposalMethod
              ),
            }}
          ></Controller>

          <Controller
            as={<NumberFormat allowNegative={true} />}
            thousandSeparator
            customInput={TextField}
            name="DisposalGainLoss"
            label="Disposal Gain/Loss"
            autoComplete="off"
            margin="dense"
            className="right"
            control={control}
            helperText={errors?.DisposalGainLoss?.message}
            error={errors?.DisposalGainLoss ? true : false}
            defaultValue={0}
            decimalScale={2}
            fixedDecimalScale
            InputProps={{ readOnly: true }}
            disabled
          ></Controller>

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="Reason"
            value={voiceRemark}
            setValue={setValue}
            record={record}
            required
            setRecord={setRecord}
            control={control}
            controllerProps={{
              error: errors?.Reason ? true : false,
              helperText: errors?.Reason?.message,
              ref: register,
              autoComplete: 'off',
            }}
          />

          <div style={{ width: '100%', marginTop: '24px' }}>
            <FileUploadInput
              placeholder={previewFiles.length === 0 ? 'Attachment' : null}
              label={previewFiles.length > 0 ? 'Attachment' : null}
              name="Attachment"
              files={files}
              onHandleUploadChange={handleUploadChange}
              multiple
              accept={
                '.png, .jpg, .jpeg, .gif, .mp4, .avi, .mkv, .mov, .flv, .3gp, application/msword, application/pdf, application/vnd.ms-excel, application/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              }
              imagePreview={
                <>
                  {previewFiles?.map((v, i) => (
                    <UploadPreview
                      remove
                      key={v}
                      src={v}
                      onClick={() => removeFile(i)}
                      mediaType={
                        files[i]?.type ?? DocData?.DocumentListing[i]?.mediaType
                      }
                    />
                  ))}
                </>
              }
            />
          </div>
        </CardContents>
      </ContentWrapper>

      <Footer
        options={[
          {
            name: 'Save As Draft',
            onClick: handleSubmit(data =>
              onSubmit(data, ApprovalStatus.Active)
            ),
            color: 'primary',
          },
          {
            name: 'Submit',
            onClick: handleSubmit(data =>
              onSubmit(data, ApprovalStatus.Submit)
            ),
            color: 'primary',
          },
        ]}
      />

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error'}
      />
    </>
  )
}
